import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getPortfolio, getFundName } from "../data/portfolios";
import Tooltip from "../components/Tooltip";

const C = {
  bg: "#0a0a0a",
  card: "#111",
  border: "rgba(255,255,255,0.08)",
  borderHover: "rgba(201,168,76,0.5)",
  gold: "#C9A84C",
  teal: "#00D4C8",
  violet: "#7C5CFC",
  white: "#F2F6FA",
  muted: "#888780",
  fog: "#6B7E91",
  red: "#FF3B5C",
  amber: "#E8C547",
  green: "#2EC8A0",
};

function fmtCur(n, cur = "USD") {
  if (!Number.isFinite(n) || n <= 0) return "$0";
  const sym = { USD: "$", INR: "\u20B9", AED: "AED\u00A0", GBP: "\u00A3" }[cur] || "$";
  if (cur === "INR") {
    if (n >= 1e7) return `${sym}${(n / 1e7).toFixed(1)}Cr`;
    if (n >= 1e5) return `${sym}${(n / 1e5).toFixed(1)}L`;
    return `${sym}${n.toLocaleString()}`;
  }
  if (n >= 1e9) return `${sym}${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(0)}M`;
  if (n >= 1e3) return `${sym}${(n / 1e3).toFixed(0)}K`;
  return `${sym}${n.toLocaleString()}`;
}

function signalMeta(signal) {
  if (signal === "red")
    return { label: "CRITICAL", color: C.red, icon: "\uD83D\uDD34", tip: "Restructuring velocity significantly below sector peers. Competitors are capturing margin faster." };
  if (signal === "amber")
    return { label: "MONITOR", color: C.amber, icon: "\uD83D\uDFE1", tip: "Transformation underway but below sector median pace. Structured intervention recommended." };
  return { label: "STABLE", color: C.green, icon: "\u2705", tip: "Transformation velocity at or above sector median. Optimise and accelerate." };
}

function HeroCard({ label, value, sub, tip }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${C.gold}`,
        borderRadius: 12,
        padding: "24px 28px",
        flex: 1,
        minWidth: 220,
      }}
    >
      <p
        style={{
          fontFamily: "'Overpass Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: C.muted,
          margin: 0,
          marginBottom: 12,
        }}
      >
        {label} {tip && <Tooltip text={tip} />}
      </p>
      <p
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 44,
          lineHeight: 1,
          color: C.gold,
          margin: 0,
          letterSpacing: "0.02em",
        }}
      >
        {value}
      </p>
      {sub && (
        <p
          style={{
            fontFamily: "'Overpass Mono', monospace",
            fontSize: 11,
            color: C.fog,
            margin: 0,
            marginTop: 8,
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function CompanyCard({ company, onView }) {
  const [hover, setHover] = React.useState(false);
  const ebitda = company.asbc_gap * 0.6;
  const meta = signalMeta(company.signal);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: C.card,
        border: `1px solid ${hover ? C.borderHover : C.border}`,
        borderRadius: 12,
        padding: "24px 24px 20px",
        transition: "border-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div>
        <p
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: C.white,
            margin: 0,
            lineHeight: 1.25,
          }}
        >
          {company.name}
        </p>
        <p
          style={{
            fontSize: 12,
            color: C.muted,
            margin: "4px 0 0",
            letterSpacing: "0.02em",
          }}
        >
          {company.sector} · {company.headcount.toLocaleString()} employees
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          paddingTop: 4,
        }}
      >
        <p
          style={{
            fontFamily: "'Overpass Mono', monospace",
            fontSize: 12,
            color: C.muted,
            margin: 0,
          }}
        >
          Gap:{" "}
          <span style={{ color: "rgba(255,255,255,0.85)" }}>
            {fmtCur(company.asbc_gap, company.currency)}
          </span>
        </p>
        <p
          style={{
            fontFamily: "'Overpass Mono', monospace",
            fontSize: 12,
            color: C.muted,
            margin: 0,
          }}
        >
          EBITDA impact: <Tooltip text="Estimated recoverable margin improvement for this company. Formula: ASBC gap × 0.6. The remaining 40% funds reinvestment in Build and Buy talent pathways." />{" "}
          <span
            style={{
              color: C.gold,
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: "0.02em",
            }}
          >
            {fmtCur(ebitda, company.currency)}
          </span>
        </p>
      </div>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          alignSelf: "flex-start",
          padding: "6px 12px",
          background: `${meta.color}14`,
          border: `1px solid ${meta.color}40`,
          borderRadius: 99,
          fontSize: 11,
          fontWeight: 600,
          color: meta.color,
          letterSpacing: "0.08em",
        }}
      >
        <span>{meta.icon}</span>
        <span>{meta.label}</span>
        <Tooltip text={meta.tip} />
      </div>

      <p
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontSize: 14,
          color: "rgba(255,255,255,0.75)",
          margin: 0,
          lineHeight: 1.5,
          fontStyle: "italic",
        }}
      >
        {company.urgency_reason}
      </p>

      <button
        onClick={() => onView(company.id)}
        style={{
          marginTop: "auto",
          padding: "10px 16px",
          background: "transparent",
          border: `1px solid ${C.gold}`,
          color: C.gold,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          letterSpacing: "0.02em",
        }}
      >
        View Deep Dive →
      </button>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const fundSlug = sessionStorage.getItem("fund_slug") || "khazanah";
  const fundName = getFundName(fundSlug);

  const companies = useMemo(
    () =>
      [...getPortfolio(fundSlug)].sort(
        (a, b) => b.asbc_gap * 0.6 - a.asbc_gap * 0.6
      ),
    [fundSlug]
  );

  const portfolioCurrency = companies[0]?.currency || "USD";
  const totalEbitda = companies.reduce((s, c) => s + c.asbc_gap * 0.6, 0);
  const redCount = companies.filter((c) => c.signal === "red").length;
  const totalCount = companies.length;

  const handleBack = () => {
    navigate(`/fund/${fundSlug}`);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.white,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Header */}
      <header
        style={{
          borderBottom: `1px solid ${C.border}`,
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 24,
              margin: 0,
              letterSpacing: "0.06em",
              color: C.white,
            }}
          >
            reAImagination
          </h1>
          <p
            style={{
              fontFamily: "'Overpass Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              color: C.gold,
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            Portfolio Intelligence
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <span
            style={{
              fontFamily: "'Overpass Mono', monospace",
              fontSize: 12,
              color: C.muted,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {fundName || "Portfolio Fund"}
          </span>
          <button
            onClick={() => navigate("/target")}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: `1px solid ${C.gold}`,
              color: C.gold,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            Target Analysis →
          </button>
          <button
            onClick={handleBack}
            style={{
              padding: "8px 14px",
              background: "transparent",
              border: `1px solid ${C.border}`,
              color: C.muted,
              borderRadius: 8,
              fontSize: 12,
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            ← Fund Home
          </button>
        </div>
      </header>

      {/* Main */}
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 32px 96px" }}>
        <div style={{ marginBottom: 40 }}>
          <p
            style={{
              fontFamily: "'Overpass Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: C.gold,
              margin: 0,
            }}
          >
            Portfolio Heat Map
          </p>
          <h2
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 44,
              margin: "6px 0 0",
              letterSpacing: "0.02em",
              color: C.white,
            }}
          >
            Where the transformation gap is largest
          </h2>
        </div>

        {/* Hero metric strip */}
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            marginBottom: 40,
          }}
        >
          <HeroCard
            label="Total Portfolio EBITDA Opportunity"
            value={fmtCur(totalEbitda, portfolioCurrency)}
            sub="ASBC gap × 0.6 across the portfolio"
            tip="Sum of ASBC transformation gaps across all portfolio companies, multiplied by 0.6 recoverability factor. ASBC (Activity-Skills Based Costing) prices tasks — not roles — across 6 workforce types to identify the optimal cost structure."
          />
          <HeroCard
            label="Companies Requiring Attention"
            value={`${redCount}`}
            sub={`${redCount} of ${totalCount} in critical signal`}
            tip="Count of portfolio companies in CRITICAL or MONITOR signal, indicating transformation velocity below sector benchmark."
          />
          <HeroCard
            label="Portfolio Companies"
            value={`${totalCount}`}
            sub="Active under this fund"
            tip="Total active companies under this fund loaded into the reAImagination platform."
          />
        </div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 20,
          }}
        >
          {companies.map((c) => (
            <CompanyCard
              key={c.id}
              company={c}
              onView={(id) => navigate(`/company/${id}`)}
            />
          ))}
        </div>

        {/* Grid footer */}
        <div
          style={{
            marginTop: 40,
            padding: "24px 0",
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <button
            style={{
              padding: "12px 22px",
              background: "transparent",
              border: `1px solid ${C.gold}`,
              color: C.gold,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: "0.02em",
            }}
          >
            + Add company to portfolio
          </button>
          <span
            style={{
              fontFamily: "'Overpass Mono', monospace",
              fontSize: 11,
              color: C.muted,
              letterSpacing: "0.04em",
            }}
          >
            Max 3 additions in demo mode
          </span>
        </div>
      </main>
    </div>
  );
}
